
import { Arr } from '../../../CodesForFoundation';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { EditorModeInfo } from '../../canvas/EditorModeInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { ParticipantManager } from '../../canvas/participants/ParticipantManager';
import { MenuWidget } from '../../canvas/workspace/widgets/WidgetInterface';
import { WidgetManager } from '../../canvas/workspace/widgets/WidgetManager';
import { ActiveObject, resolve } from '../../Container';
import { AutoSpotlightInfo, AutoSpotlightMode } from './SpotlightProperties';

export class SpotlightMechanics extends ActiveObject {

    @resolve(CommandDispatcher) private dispatch!: CommandDispatcher;
    @resolve(EditorSession) private session!: EditorSession;
    @resolve(ParticipantManager) private participants!: ParticipantManager;
    @resolve(WidgetManager) private widgets!: WidgetManager;

    public start = () => this.burden(
        this.session.observe(AutoSpotlightInfo, this.syncAutoSpotlightWidget),
        this.session.observe(EditorModeInfo, this.syncAutoSpotlightWidget),
        // this.session.observe(ManualSpotlightInfo, this.onSpotlightInfoChange),
    );

    private syncAutoSpotlightWidget = () => {
        const asi = this.session.get(AutoSpotlightInfo);
        if (asi.enabled) {
            this.realizeWidgets();
            this.updateWidgets(asi);
        }
        else {
            this.destroyWidgets();
        }
    };

    private realizeWidgets() {
        const { widgets } = this;
        if (widgets.has('spotlight')) return;
        const panel = widgets.createContainer({
            name: 'spotlight',
            position: ['docked', 'top', 'stack'],
        });
        panel.add(
            widgets.createButton({
                text: 'Previous',
                image: 'deqo://icons/hero/chevron-left',
                onAction: this.onPrev,
            }),
            widgets.createMenu({
                name: 'userMenu',
                items: [
                    widgets.createButton({
                        name: 'objects',
                        text: 'Objects',
                        active: true,
                        onAction: () => this.onSwitchMode('objects'),
                    }),
                    widgets.createButton({
                        name: 'votes',
                        text: 'Votes',
                        onAction: () => this.onSwitchMode('votes'),
                    }),
                    widgets.createButton({ text: '-' }),
                    widgets.createButton({
                        text: 'Stop',
                        onAction: this.onStop,
                    })
                ],
                itemsWidth: 10,
            }),
            widgets.createButton({
                text: 'Stop',
                image: 'deqo://icons/hero/x',
                onAction: this.onStop,
            }),
            widgets.createButton({
                text: 'Next',
                image: 'deqo://icons/hero/chevron-right',
                onAction: this.onNext,
            })
        );
        widgets.add(panel);
    }

    private destroyWidgets = () => {
        const { widgets } = this;
        const panel = widgets.get('spotlight');
        if (panel) widgets.remove(panel);
    };

    private updateWidgets = ({ target, mode }: AutoSpotlightInfo) => {
        const { participants, widgets } = this;
        const menu = widgets.get('spotlight')?.get<MenuWidget>('userMenu');
        if (!menu) return;

        // Update menu text
        {
            const ptpt = participants.get(target);
            menu.update({ text: ptpt?.name ?? 'Unknown', });
        }

        // Update menu checked states
        {
            const votes = menu.items.find(x => x.name === 'votes');
            votes.update({ active: mode === 'votes' });
            const objects = menu.items.find(x => x.name === 'objects');
            objects.update({ active: mode === 'objects' });
        }
    };

    private onStop = () => this.session.patch(AutoSpotlightInfo, { enabled: false, target: '' });

    private onSwitchMode = (mode: AutoSpotlightMode) => this.session.patch(AutoSpotlightInfo, { mode });

    private onNext = () => this.gotoParticipant(+1);

    private onPrev = () => this.gotoParticipant(-1);

    private gotoParticipant(amount: number) {
        // TODO: ParticipantManager is a real pain to work with
        const { target } = this.session.get(AutoSpotlightInfo);
        const ordered = this.participants.getState().slice(0).sort((a, b) => a.name.localeCompare(b.name));
        const i = ordered.findIndex(x => x.id === target);
        const next = Arr.at(ordered, i + amount)?.id || '';
        this.session.patch(AutoSpotlightInfo, { target: next });
    }
}
